import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  CheckCircle2,
  FileCode,
  Gift,
  MessageSquare,
  Music2,
  Ticket,
  Trophy,
  Volume2,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import { useGuildRights, type EntryKey } from '@/lib/access';
import {
  BOT_PLUGINS,
  PLUGIN_GROUPS,
  getDisabledPlugins,
  isHiddenByToggles,
  type PluginKey,
} from '@/lib/plugins';

/**
 * Server overview — the index route under /server/:guildId/. Compact stat row
 * plus quick-jump tiles for the most-used features. All deeper navigation
 * lives in the ServerLayout sub-sidebar, so this page stays a landing pad
 * rather than a mega-menu.
 */

interface GuildSettingsResponse {
  settings: {
    guildId: string;
    displayName?: string;
    iconUrl?: string | null;
    prefix: string;
    language: string;
    enabledPlugins: string[];
    moduleConfigs: Record<string, unknown>;
    /** Plugins the bot would act on (computed in web-api lib/pluginStatus). */
    configuredPlugins?: string[];
  };
}

interface QuickTile {
  to: string;
  label: string;
  description: string;
  icon: typeof Ticket;
  /** Hidden while this plugin is disabled in the Admin-Panel. */
  plugin?: PluginKey;
}

export function ServerDashboard() {
  const { guildId } = useParams<{ guildId: string }>();
  const { data, isLoading, error } = useQuery({
    queryKey: ['guildSettings', guildId],
    queryFn: () => api<GuildSettingsResponse>(`/api/guilds/${guildId}/settings`),
    enabled: !!guildId,
  });
  const { can } = useGuildRights(guildId);

  if (isLoading) return <p className="text-muted-foreground">Lade Server-Konfiguration…</p>;
  if (error) return <p className="text-destructive">Fehler: {(error as Error).message}</p>;
  if (!data) return null;

  const { settings } = data;
  const disabled = new Set(getDisabledPlugins(settings.moduleConfigs));
  const configured = new Set(settings.configuredPlugins ?? []);
  const activeCount = BOT_PLUGINS.filter((p) => configured.has(p.key) && !disabled.has(p.key)).length;

  const allTiles: QuickTile[] = [
    { to: `/server/${guildId}/tickets`, label: 'Tickets', description: 'Support-Threads einsehen und beantworten', icon: Ticket, plugin: 'ticketList' },
    { to: `/server/${guildId}/leaderboard`, label: 'XP-Rangliste', description: 'Top-Chatter im Server', icon: Trophy, plugin: 'leaderboard' },
    { to: `/server/${guildId}/voice`, label: 'Voice-Belegung', description: 'Wer ist gerade in welchem Kanal', icon: Volume2, plugin: 'voice' },
    { to: `/server/${guildId}/message-rate`, label: 'Nachrichten-Rate', description: 'Live-Chart der letzten 60 Minuten', icon: Activity, plugin: 'messageRate' },
    { to: `/server/${guildId}/embed`, label: 'Embed-Composer', description: 'Reiche Nachrichten mit Preview posten', icon: FileCode, plugin: 'embed' },
    { to: `/server/${guildId}/music`, label: 'Music', description: 'Bot-Player fernsteuern', icon: Music2, plugin: 'music' },
    { to: `/server/${guildId}/giveaways`, label: 'Giveaways', description: 'Laufende und beendete Verlosungen', icon: Gift, plugin: 'giveaways' },
    { to: `/server/${guildId}/warnings`, label: 'Warnungen', description: 'Moderations-Historie', icon: MessageSquare, plugin: 'moderation' },
  ];
  const tiles = allTiles.filter(
    (t) => !t.plugin || (!isHiddenByToggles(disabled, t.plugin) && can(t.plugin as EntryKey, 'view'))
  );

  return (
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center gap-4">
        {settings.iconUrl && (
          <img src={settings.iconUrl} alt="" className="h-14 w-14 rounded-full" />
        )}
        <div className="min-w-0">
          <h1 className="truncate text-2xl font-semibold">
            {settings.displayName || settings.guildId}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <code>{settings.guildId}</code> · Prefix{' '}
            <code>{settings.prefix}</code> · Sprache {settings.language}
          </p>
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-4">
        <StatTile label="Aktive Plugins" value={`${activeCount} / ${BOT_PLUGINS.length}`} />
        <StatTile label="Ausgeschaltet (Admin-Panel)" value={disabled.size} />
        <StatTile label="Sprache" value={settings.language.toUpperCase()} />
        <StatTile label="Prefix" value={settings.prefix} />
      </div>

      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Schnellzugriff
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {tiles.map((tile) => (
          <Link key={tile.to} to={tile.to}>
            <Card className="h-full transition-colors hover:border-primary">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base">
                  <tile.icon className="h-4 w-4 text-primary" />
                  {tile.label}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-muted-foreground">{tile.description}</p>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* Bot plugins only — dashboard pages already have the quick tiles above. */}
      {PLUGIN_GROUPS.filter((g) => BOT_PLUGINS.some((p) => p.group === g.key)).map((group) => (
        <div key={group.key}>
          <h2 className="mb-3 mt-8 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
            {group.label}
          </h2>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
            {BOT_PLUGINS.filter((p) => p.group === group.key).map((p) => {
              // Disabled (Admin-Panel) trumps configured: grey, never green.
              const state = disabled.has(p.key) ? 'disabled' : configured.has(p.key) ? 'configured' : 'open';
              // Link only where the user may at least view the page.
              const linkable = !!p.path && can(p.key as EntryKey, 'view');
              const card = (
                <Card
                  className={cn(
                    'h-full transition-colors',
                    state === 'configured' && 'border-emerald-500/60',
                    state === 'disabled' && 'opacity-60',
                    linkable && 'hover:border-primary'
                  )}
                >
                  <CardContent className="flex items-start justify-between gap-2 pt-4 pb-4">
                    <div className="min-w-0">
                      <div className="truncate text-sm font-medium">{p.label}</div>
                      <div className="text-xs text-muted-foreground">
                        {state === 'disabled'
                          ? 'Deaktiviert'
                          : state === 'configured'
                            ? p.needsConfig
                              ? 'Konfiguriert'
                              : 'Bereit'
                            : 'Nicht eingerichtet'}
                      </div>
                    </div>
                    {state === 'configured' && <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-500" />}
                  </CardContent>
                </Card>
              );
              return linkable ? (
                <Link key={p.key} to={`/server/${guildId}${p.path}`}>
                  {card}
                </Link>
              ) : (
                <div key={p.key}>{card}</div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
}

function StatTile({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number;
  className?: string;
}) {
  return (
    <Card className={cn(className)}>
      <CardContent className="pt-6">
        <div className="text-2xl font-semibold">{value}</div>
        <div className="text-xs text-muted-foreground">{label}</div>
      </CardContent>
    </Card>
  );
}
